import { useEffect, useState } from "react";

let rowSeq = 0;
const withKey = (e) => ({ ...e, _key: `row-${++rowSeq}` });
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, Trash2, Loader2, Save, Send, ArrowLeft, Info, Lock, CalendarDays, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { AutoGrowTextarea } from "@/components/AutoGrowTextarea";
import { ProjectTaskField } from "@/components/ProjectTaskField";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ENTRY_TYPES, LEAVE_TYPES, currentWeekEnding, weekLabel, periodStart, periodEnd, isPartWeek, nextWorkday, weekdays,
  dayLabel, computeSummary, fmtHrs, monthEndOf, monthLabel, prettyDate,
} from "@/lib/timesheet";

const SUMMARY_ROWS = [
  ["standard", "Standard"],
  ["overtime", "Overtime"],
  ["leave", "Leave"],
];

export default function TimesheetEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const initialWeek = params.get("week") ? periodEnd(params.get("week")) : currentWeekEnding();

  const [weekEnding, setWeekEnding] = useState(initialWeek);
  const [roleTeam, setRoleTeam] = useState(user?.position || "");
  const [entries, setEntries] = useState(() => [withKey({ date: periodStart(initialWeek), type: "Standard hours", project: "", hours: 7.5, notes: "" })]);
  const [status, setStatus] = useState("draft");
  const [managerComment, setManagerComment] = useState("");
  const [lockedMonths, setLockedMonths] = useState([]);
  const [tsId, setTsId] = useState(isNew ? null : id);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState({ projects: [], clients: [] });

  useEffect(() => {
    api.get("/timesheets/suggestions").then(({ data }) => {
      setSuggestions(data);
      const recent = data.clients?.[0];
      if (isNew && recent) { setEntries((es) => es.map((e) => (e.client ? e : { ...e, client: recent }))); }
    }).catch(() => {});
    if (isNew) { api.get("/months/locked").then(({ data }) => setLockedMonths(data)).catch(() => {}); }
  }, [isNew]);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/timesheets/${id}`);
        setWeekEnding(data.week_ending);
        setRoleTeam(data.role_team || "");
        setEntries(data.entries.length ? data.entries.map((e) => withKey({ ...e, _locked: (data.locked_months || []).includes((e.date || "").slice(0, 7)) })) : [withKey({ date: periodStart(data.week_ending), type: "Standard hours", project: "", hours: 7.5, notes: "" })]);
        setStatus(data.status);
        setManagerComment(data.manager_comment || "");
        setLockedMonths(data.locked_months || []);
        setTsId(data.id);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
        navigate("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, navigate, user]);

  const readOnly = status === "final";
  const isLocked = (e) => readOnly || !!e._locked;
  const inLockedMonth = (e) => lockedMonths.includes((e.date || "").slice(0, 7));
  const anyLocked = entries.some((e) => e._locked);
  const monthEnd = monthEndOf(weekEnding) && !lockedMonths.includes(monthEndOf(weekEnding)) ? monthEndOf(weekEnding) : null;
  const changeWeek = (v) => {
    if (!v) return;
    const end = periodEnd(v);
    const days = weekdays(end);
    setWeekEnding(end);
    setEntries((es) => {
      const inside = (e) => e._locked || (e.date >= days[0] && e.date <= end);
      const free = days.filter((d) => !es.some((e) => inside(e) && e.date === d));
      const out = es.map((e) => (inside(e) ? e : free.length ? { ...e, date: free.shift() } : null)).filter(Boolean);
      return out.length ? out : es.slice(0, 1).map((e) => ({ ...e, date: days[0] }));
    });
  };
  const summary = computeSummary(entries, weekEnding);

  const updateEntry = (i, f, v) => setEntries((es) => es.map((e, idx) => (idx === i ? { ...e, [f]: v } : e)));
  const lastField = (es, f) => [...es].reverse().find((e) => e[f] && !LEAVE_TYPES.includes(e.type))?.[f] || "";
  const lastClient = (es) => lastField(es, "client") || suggestions.clients[0] || "";
  const addEntry = () => setEntries((es) => {
    const last = es[es.length - 1];
    return [...es, withKey({ date: last ? nextWorkday(last.date) : periodStart(weekEnding), type: "Standard hours", client: lastClient(es), project: lastField(es, "project"), hours: 7.5, notes: "" })];
  });
  const prefillWeek = () => setEntries((es) => {
    const client = lastClient(es);
    const project = lastField(es, "project");
    return weekdays(weekEnding).map((d) => withKey({ date: d, type: "Standard hours", client, project, hours: 7.5, notes: "" }));
  });
  const removeEntry = (i) => setEntries((es) => (es.length === 1 ? es : es.filter((_, idx) => idx !== i)));

  const buildPayload = () => ({
    week_ending: weekEnding,
    role_team: roleTeam,
    entries: entries.map((e) => ({
      date: e.date, type: e.type,
      client: LEAVE_TYPES.includes(e.type) ? "" : (e.client || "").trim(),
      project: LEAVE_TYPES.includes(e.type) ? "" : e.project,
      hours: parseFloat(e.hours) || 0, notes: e.notes || "",
    })),
  });

  const persist = async () => {
    const payload = buildPayload();
    if (tsId) { const { data } = await api.put(`/timesheets/${tsId}`, payload); return data; }
    const { data } = await api.post("/timesheets", payload); setTsId(data.id); return data;
  };

  const run = async (fn, to = "/") => { setSaving(true); try { await fn(); navigate(to); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); } };
  const onSaveDraft = () => run(async () => { await persist(); toast.success("Saved"); });
  const onSubmit = () => run(async () => { const s = await persist(); await api.post(`/timesheets/${s.id}/submit`, {}); toast.success("Submitted for approval"); });
  const onMonthEnd = () => run(async () => {
    const s = await persist();
    if (["draft", "rejected"].includes(s.status) && total > 0) await api.post(`/timesheets/${s.id}/submit`, {});
  }, `/month/${monthEnd}`);

  if (loading) return <Layout><div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div></Layout>;

  const total = summary.total_worked + summary.total_leave;

  return (
    <Layout>
      <button onClick={() => navigate("/")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">{isNew ? "New Weekly Timesheet" : weekLabel(weekEnding)}</h1>
        <StatusBadge status={status} />
      </div>
      <p className="mt-1 text-muted-foreground">Record your hours for the week and submit them for approval. The invoice number and declaration go in your month-end submission.</p>

      {status === "rejected" && managerComment && (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
          <span className="font-semibold">Returned - </span>{managerComment}
        </div>
      )}
      {readOnly && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
          <Lock className="h-4 w-4" /> This timesheet has been finalised and is locked.
        </div>
      )}
      {!readOnly && anyLocked && (
        <div data-testid="locked-days-banner" className="mt-4 flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
          <Lock className="h-4 w-4" /> Days in {lockedMonths.map(monthLabel).join(", ")} are locked because that month has been submitted.
        </div>
      )}

      <Card className="mt-6 p-5">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2"><Label>Name</Label><Input value={user?.name || ""} disabled className="bg-muted/50" data-testid="timesheet-name-input" /></div>
          <div className="space-y-2"><Label htmlFor="we">Week ending</Label><Input id="we" data-testid="timesheet-week-picker" type="date" value={weekEnding} disabled={readOnly} onChange={(e) => changeWeek(e.target.value)} className="font-mono" /></div>
          <div className="space-y-2"><Label htmlFor="rt">Role / team</Label><Input id="rt" data-testid="timesheet-role-input" value={roleTeam} disabled={readOnly} onChange={(e) => setRoleTeam(e.target.value)} placeholder="e.g. Consultant" /></div>
        </div>
        {isPartWeek(weekEnding) && (
          <p data-testid="part-week-note" className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            This week is split at the month end, so it only covers {prettyDate(periodStart(weekEnding))} - {prettyDate(weekEnding)} ({monthLabel(weekEnding.slice(0, 7))}). The other days go on the {weekEnding.slice(8) === "01" || periodStart(weekEnding).slice(8) === "01" ? "previous" : "next"} month's timesheet.
          </p>
        )}
      </Card>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {SUMMARY_ROWS.map(([k, l]) => (
          <Card key={k} className="p-4" data-testid={`summary-${k}`}><div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div><div className="mt-1.5 font-heading text-xl font-extrabold tabular">{fmtHrs(summary[k])}</div></Card>
        ))}
        <Card className="border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5 p-4" data-testid="summary-total_worked"><div className="font-mono text-[10px] uppercase tracking-wider text-[hsl(var(--accent))]">Total worked</div><div className="mt-1.5 font-heading text-xl font-extrabold tabular">{fmtHrs(summary.total_worked)}</div></Card>
      </div>
      {summary.dates_outside > 0 && (
        <div className="mt-3 flex items-center gap-2 text-sm text-amber-600"><Info className="h-4 w-4" />{summary.dates_outside} entr{summary.dates_outside === 1 ? "y" : "ies"} outside this week</div>
      )}

      <Card className="mt-6 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead><tr className="border-b border-border bg-secondary/40 text-left">
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
              <th className="px-2 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Day</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Client</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Project</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</th>
              <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">Hours</th>
              {!readOnly && <th className="px-2 py-3" />}
            </tr></thead>
            <tbody>
              {entries.map((e, i) => {
                const isLeave = LEAVE_TYPES.includes(e.type);
                const lockedRow = isLocked(e);
                return (
                  <tr key={e._key} className="border-b border-border last:border-0 hover:bg-secondary/20">
                    <td className="px-4 py-2" style={{ minWidth: 150 }}><Input data-testid={`entry-date-${i}`} type="date" value={e.date} disabled={lockedRow} onChange={(ev) => updateEntry(i, "date", ev.target.value)} className="h-9 font-mono" />{!lockedRow && inLockedMonth(e) && <p data-testid={`entry-locked-warning-${i}`} className="mt-1 text-xs text-rose-600">{monthLabel(e.date.slice(0, 7))} is already submitted - pick another date.</p>}</td>
                    <td className="px-2 py-2 font-mono text-sm text-muted-foreground" data-testid={`entry-day-${i}`}>{dayLabel(e.date) || "-"}</td>
                    <td className="px-3 py-2" style={{ minWidth: 150 }}>
                      {lockedRow ? <span className="text-sm">{e.type}</span> : (
                        <Select value={e.type} onValueChange={(v) => updateEntry(i, "type", v)}>
                          <SelectTrigger data-testid={`entry-type-${i}`} className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{ENTRY_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`entry-type-${i}-option-${t.replace(/\s/g, "-")}`}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-3 py-2" style={{ minWidth: 180 }}><ProjectTaskField index={i} testPrefix="entry-client" value={isLeave ? "" : (e.client || "")} suggestions={suggestions.clients} disabled={lockedRow || isLeave} onChange={(v) => updateEntry(i, "client", v)} placeholder={isLeave ? "-" : "Client"} /></td>
                    <td className="px-3 py-2" style={{ minWidth: 200 }}><ProjectTaskField index={i} value={isLeave ? "" : e.project} suggestions={suggestions.projects} disabled={lockedRow || isLeave} onChange={(v) => updateEntry(i, "project", v)} placeholder={isLeave ? "-" : "Project"} /></td>
                    <td className="px-3 py-2" style={{ minWidth: 260 }}><AutoGrowTextarea data-testid={`entry-notes-${i}`} value={e.notes} disabled={lockedRow} onChange={(ev) => updateEntry(i, "notes", ev.target.value)} placeholder="What you worked on" /></td>
                    <td className="px-3 py-2"><Input data-testid={`entry-hours-${i}`} type="number" step="0.5" min="0" max="24" value={e.hours} disabled={lockedRow} onChange={(ev) => updateEntry(i, "hours", ev.target.value)} className="h-9 w-20 text-center font-mono font-semibold tabular" /></td>
                    {!readOnly && <td className="px-2 py-2 text-center">{!lockedRow && <Button data-testid={`entry-remove-${i}`} variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600" onClick={() => removeEntry(i)}><Trash2 className="h-4 w-4" /></Button>}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {!readOnly && (
        <>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button data-testid="entry-add-btn" variant="outline" onClick={addEntry} className="gap-2"><Plus className="h-4 w-4" /> Add Entry</Button>
            {!anyLocked && <Button data-testid="entry-prefill-week-btn" variant="outline" onClick={prefillWeek} className="gap-2"><CalendarDays className="h-4 w-4" /> {isPartWeek(weekEnding) ? "Fill week days" : "Fill Mon-Fri"}</Button>}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="flex-1" />
            <Button data-testid="timesheet-save-draft-btn" variant="secondary" onClick={onSaveDraft} disabled={saving} className="gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Draft</Button>
            <Button data-testid="timesheet-submit-btn" variant={monthEnd ? "outline" : "default"} onClick={onSubmit} disabled={saving || total <= 0 || ["submitted", "approved"].includes(status)} className="gap-2"><Send className="h-4 w-4" /> {["submitted", "approved"].includes(status) ? "Submitted" : "Submit for approval"}</Button>
            {monthEnd && (
              <Button data-testid="timesheet-month-end-btn" onClick={onMonthEnd} disabled={saving} className="gap-2"><CalendarCheck className="h-4 w-4" /> Month-end submission - {monthLabel(monthEnd)}</Button>
            )}
          </div>
        </>
      )}
    </Layout>
  );
}
